import {useState} from "react";
import {PropertyTypology} from "../constants/property.typology.ts";
import searchIcon from "../assets/icons/search-icon.svg";
import provinces from "../assets/json/provincie_italiane.json";
import {useTranslation} from "react-i18next";
import {PropertyConfidentialNegotiation} from "../constants/property.confidentialNegotiation.ts";
import {PropertyCountry} from "../constants/property.country.ts";

export default function OffMarketSearchForm({onSearch}) {
    const {t} = useTranslation();

    const [country, setCountry] = useState('');
    const [province, setProvince] = useState('');
    const [typology, setTypology] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [minPrice, setMinPrice] = useState('');
    const [disablePrice, setDisablePrice] = useState(false);
    const [confidentialNegotiation, setConfidentialNegotiation] = useState('');
    const [disableLocation, setDisableLocation] = useState(false);

    const handleMinPriceChange = (e) => {
        const rawValue = e.target.value.replace(/\D/g, ""); // Rimuove tutto tranne i numeri
        const formattedValue = rawValue > 0 ? new Intl.NumberFormat("it-IT").format(rawValue) : ''; // Formatta il numero con separatori italiani
        setMinPrice(formattedValue);
    };

    const handleMaxPriceChange = (e) => {
        const rawValue = e.target.value.replace(/\D/g, ""); // Rimuove tutto tranne i numeri
        const formattedValue = rawValue > 0 ? new Intl.NumberFormat("it-IT").format(rawValue) : ''; // Formatta il numero con separatori italiani
        setMaxPrice(formattedValue);
    };

    const handleKeyDown = (e) => {
        // Blocca l'input di caratteri non numerici (ma consente backspace, delete, etc.)
        if (
            ["e", "E", "+", "-"].includes(e.key) || // Blocca caratteri invalidi
            (e.key === "." && e.target.value.includes(".")) // Blocca più di un punto decimale
        ) {
            e.preventDefault();
        }
    };

    const handleClick = () => {
        const filters = {
            province: province ?? '',
            typology: typology ?? '',
            maxPrice: maxPrice ?? '',
            minPrice: minPrice ?? '',
            status: 'offMarket',
            confidentialNegotiation: confidentialNegotiation,
            country: country,
        }
        onSearch(1, filters, true);
    };

    const handleConfidentialNegotiationChange = (e) => {
        const value = e.target.value;
        setConfidentialNegotiation(value);
        setDisablePrice(value === 'true');
        if (value === 'true') {
            setMinPrice("");
            setMaxPrice("");
        }
    }

    const handleCountryChange = (e) => {
        const value = e.target.value;
        setCountry(value);
        if (value === null || value !== "IT") {
            setDisableLocation(true);
            setProvince('');
        } else {
            setDisableLocation(false);
            setProvince('');
        }
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-y-10 w-full md:gap-x-[40px]">
            <select
                value={country}
                onChange={handleCountryChange}
                className="w-full"
            >
                <option value="">
                    {t('offMarketProperty.searchForm.labels.country')}
                </option>
                {Object.entries(PropertyCountry).map(([key, country]) => (
                    <option key={key} value={country}>
                        {t(`property.countries.${country}`)}
                    </option>
                ))}
            </select>
            <select
                value={province}
                onChange={(e) => setProvince(e.target.value)}
                className="w-full"
                disabled={disableLocation}
            >
                <option value={null}>
                    {t('offMarketProperty.searchForm.labels.location')}
                </option>
                {Object.entries(provinces).map(([key, province]) => (
                    <option key={key} value={province.code}>
                        {province.name}
                    </option>
                ))}
            </select>
            <select className="w-full md:w-auto" value={typology} onChange={(e) => setTypology(e.target.value)}>
                <option value={null}>{t('offMarketProperty.searchForm.labels.typology')}</option>
                {Object.entries(PropertyTypology).map(([key, typology]) => (
                    <option key={key} value={typology}>
                        {t(`property.typologies.${typology}`)}
                    </option>
                ))}
            </select>
            <select
                value={confidentialNegotiation}
                onChange={handleConfidentialNegotiationChange}
                className="w-full md:w-auto"
            >
                <option value={""}>
                    {t('properties.searchForm.labels.confidentialNegotiation')}
                </option>
                {Object.entries(PropertyConfidentialNegotiation).map(([key, typology]) => (
                    <option key={key} value={typology}>
                        {t(`properties.searchForm.values.confidentialNegotiations.${typology}`)}
                    </option>))}
            </select>
            <div className={`flex items-end 
            ${disablePrice ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <input
                    type="text"
                    value={minPrice}
                    onChange={handleMinPriceChange}
                    onKeyDown={handleKeyDown}
                    placeholder={t('offMarketProperty.searchForm.labels.minPrice')}
                    className="w-full disabled:cursor-not-allowed"
                    disabled={disablePrice}
                />
                <span className={'font-raleway text-[18px] md:text-[16px] text-secondary border-b pb-[15px]'}>€</span>
            </div>
            <div className={`flex items-end 
                ${disablePrice ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <input
                    type="text"
                    value={maxPrice}
                    onChange={handleMaxPriceChange}
                    onKeyDown={handleKeyDown}
                    placeholder={t('offMarketProperty.searchForm.labels.maxPrice')}
                    className="w-full disabled:cursor-not-allowed"
                    disabled={disablePrice}
                />
                <span className={'font-raleway text-[18px] md:text-[16px] text-secondary border-b pb-[15px]'}>€</span>
            </div>
            <div className={'md:grid md:grid-cols-subgrid md:col-span-3'}>
                <button
                    onClick={handleClick}
                    className="btn-form w-full md:!text-[16px] md:col-start-3"
                >
                    <div className="flex justify-between pb-[5px]">
                        <span>{t('offMarketProperty.searchForm.find')}</span>
                        <img src={searchIcon} alt="search"/>
                    </div>
                </button>
            </div>
        </div>
    );
}
